import Modal from "@/atomic/atoms/Modal";
import { Invoice } from "@/hooks/useTicket";
import { useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";


const DebtContent = ({ invoice }: { invoice: Invoice | null }) => {
  const { t, Trans } = useI18n();
  const { theme } = useThemeContext();

  if (!invoice) {
    return <div style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>{t("pages:receipt:withoutDebt")}</div>;
  }

  return (
    <div className="overflow-auto max-h-[calc(100vh-300px)] mt-6 mb-3">
      <p
        className="text-center font-bold text-[22px] mb-5 tracking-[-0.2px] leading-[1.2]"
        style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
      >
        {t("pages:receipt:title")}
      </p>
      <p
        className="text-center text-base leading-[20.8px]"
        style={{ color: theme.colors.text, fontFamily: theme.fonts.secondary }}
      >
        <Trans i18nKey="pages:receipt:paymentSuccess" components={[<b></b>]} values={{ price: invoice?.price }} />
      </p>

      <div className="flex flex-col gap-[5px] mt-5 w-full">
        <p
          className="font-medium text-base leading-[20.8px] tracking-[-0.4px]"
          style={{ color: theme.colors.text, fontFamily: theme.fonts.secondary }}
        >
          {t("pages:receipt:transactionData")} #{invoice?.invoiceId}
        </p>

        {invoice?.debts?.map((debt, index) => (
          <div key={index} className="w-full">
            <p
              className="font-medium text-base leading-[20.8px] tracking-[-0.4px]"
              style={{ color: theme.colors.text, fontFamily: theme.fonts.secondary }}
            >
              {debt.plate}
            </p>
            <div className="w-full h-px my-[5px] bg-[#e9eaec]" />
            <div className="flex flex-col">
              <p
                className="text-xs leading-[16.8px] tracking-[-0.4px]"
                style={{ color: '#0e0d0d', fontFamily: theme.fonts.secondary }}
              >
                {debt.fmtDataTransacao}
              </p>
              <p
                className="text-xs leading-[16.8px] tracking-[-0.4px]"
                style={{ color: '#0e0d0d', fontFamily: theme.fonts.secondary }}
              >
                {debt.owner}
              </p>
              <p
                className="text-xs leading-[16.8px] tracking-[-0.4px]"
                style={{ color: '#0e0d0d', fontFamily: theme.fonts.secondary }}
              >
                {debt.nmPraca}
              </p>
            </div>
            <div className="w-full h-px my-[5px] bg-[#e9eaec]" />
            <div className="flex justify-between items-center h-[22px]">
              <p
                className="text-sm leading-[18.2px] tracking-[-0.4px]"
                style={{ color: theme.colors.text, fontFamily: theme.fonts.secondary }}
              >
                {t("pages:receipt:passage")}
              </p>
              <p
                className="text-base leading-[1.4] text-right px-1"
                style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
              >
                R$ {debt.originalPrice}
              </p>
            </div>
            <div className="flex justify-between items-center h-[22px]">
              <p
                className="text-sm leading-[18.2px] tracking-[-0.4px]"
                style={{ color: theme.colors.text, fontFamily: theme.fonts.secondary }}
              >
                {t("pages:receipt:fines")}
              </p>
              <p
                className="text-base leading-[1.4] text-right px-1"
                style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
              >
                R$ {debt.fine || '0,00'}
              </p>
            </div>
            <div className="flex justify-between items-center h-[22px]">
              <p
                className="text-base font-bold leading-[1.4]"
                style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
              >
                {t("pages:receipt:total")}
              </p>
              <p
                className="text-base font-bold leading-[1.4] text-right px-1"
                style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}
              >
                R$ {debt.price}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TicketModal = ({
  toggleModal,
  generatePDF,
  toggleModalRegister,
  invoice,
}: {
  toggleModal: () => void;
  toggleModalRegister?: () => void;
  generatePDF: () => void;
  invoice: Invoice | null;
}) => {
  const { t, Trans } = useI18n();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);
  const { themeName, theme } = useThemeContext();
  const isZapay = themeName === 'zapay';

  const isAuthenticated = !!customer?.client?.id;
  
  if (!isAuthenticated) {
    return (
      <Modal
        content={<div className="flex flex-col">
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-11 h-1 bg-[#bfbfbf] rounded-[10px]"></div>
          <button
            type="button"
            onClick={toggleModal}
            className="absolute top-6 right-6 text-black"
          >
            <img src="/svg/icon-close.svg" alt="Close" className="w-4 h-4" />
          </button>
          <DebtContent invoice={invoice} />

          <div className="flex flex-col gap-5">
            <button
              className="bg-white hover:bg-gray-50 border p-2 h-[60px] w-full font-bold text-base tracking-[-0.1px]"
              style={{
                color: isZapay ? theme.colors.primary : '#0e0d0d',
                borderColor: isZapay ? theme.colors.primary : '#363636',
                borderRadius: isZapay ? theme.borderRadius.medium : '12px',
                fontFamily: theme.fonts.primary
              }}
              type="button"
              onClick={generatePDF}>
              {t("pages:receipt:downloadReceipt")}
            </button>

            {!isZapay && (
              <>
                <p
                  className="text-[15px] leading-[18.2px] tracking-[-0.4px]"
                  style={{
                    color: '#525252',
                    fontFamily: theme.fonts.secondary
                  }}
                >
                  <Trans i18nKey="pages:receipt:downloadReceiptDescription" components={[<b></b>, <b></b>]} />
                </p>

                <button
                  className="text-white p-2 h-[60px] w-full font-bold text-base tracking-[-0.1px]"
                  style={{
                    backgroundColor: theme.colors.primary,
                    borderRadius: '12px',
                    fontFamily: theme.fonts.primary
                  }}
                  type="button"
                  onClick={toggleModalRegister}>
                  {t("pages:receipt:register")}
                </button>
              </>
            )}
          </div>
        </div>}
      />
    );
  }

  return (
    <Modal
      content={<div className="flex flex-col">
        <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-11 h-1 bg-[#bfbfbf] rounded-[10px]"></div>
        <button
          type="button"
          onClick={toggleModal}
          className="absolute top-6 right-6 text-black"
        >
          <img src="/svg/icon-close.svg" alt="Close" className="w-4 h-4" />
        </button>

        <DebtContent invoice={invoice} />

        <button
          className="border p-2 h-[60px] w-full font-bold text-base tracking-[-0.1px]"
          style={{
            color: isZapay ? 'white' : '#0e0d0d',
            backgroundColor: isZapay ? theme.colors.primary : 'white',
            borderColor: isZapay ? theme.colors.primary : '#363636',
            borderRadius: isZapay ? theme.borderRadius.medium : '12px',
            fontFamily: theme.fonts.primary
          }}
          type="button"
          onClick={generatePDF}>
          {t("pages:receipt:downloadReceipt")}
        </button>
      </div>}
    />
  );
};

export default TicketModal;
